import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { 
  Box, 
  Grid, 
  TextField, 
  Button, 
  Card, 
  CardContent, 
  Typography, 
  Chip,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  PlayArrow,
  Settings,
  Add,
  DragIndicator,
  Psychology,
  AutoAwesome,
  Insights,
  AccountTree,
  Science,
  ModelTraining,
  IntegrationInstructions,
  SmartToy,
  Analytics,
  Download,
  Upload,
  ContentCopy,
  Share,
  Help
} from '@mui/icons-material';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Line, Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  RadialLinearScale,
  Filler,
  Tooltip as ChartTooltip,
  Legend,
  Title
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  RadialLinearScale,
  Filler,
  ChartTooltip,
  Legend,
  Title
);

// AI Model Configuration
const AI_MODELS = {
  GPT4: {
    id: 'GPT4',
    name: 'GPT-4 Turbo',
    provider: 'OpenAI',
    description: 'Most powerful model for creative tasks and complex reasoning',
    capabilities: ['creativity', 'reasoning', 'language'],
    icon: <AutoAwesome />,
    color: '#00f2ff',
    contextWindow: 128000,
    isActive: true
  },
  CLAUDE3: {
    id: 'CLAUDE3',
    name: 'Claude 3 Opus',
    provider: 'Anthropic',
    description: 'Excellent for long-form content and detailed analysis',
    capabilities: ['analysis', 'long-form', 'safety'],
    icon: <Insights />,
    color: '#ff00d6',
    contextWindow: 200000,
    isActive: true
  },
  GEMINI: {
    id: 'GEMINI',
    name: 'Gemini Ultra',
    provider: 'Google',
    description: 'Strong multimodal capabilities and research proficiency',
    capabilities: ['multimodal', 'research', 'knowledge'],
    icon: <AccountTree />,
    color: '#ffcc00',
    contextWindow: 1000000,
    isActive: true
  },
  DEEPSEEK: {
    id: 'DEEPSEEK',
    name: 'DeepSeek Coder',
    provider: 'DeepSeek',
    description: 'Specialized for coding tasks with large context window',
    capabilities: ['coding', 'debugging', 'technical'],
    icon: <IntegrationInstructions />,
    color: '#00ff9d',
    contextWindow: 200000,
    isActive: true
  },
  LLAMA3: {
    id: 'LLAMA3',
    name: 'Llama 3',
    provider: 'Meta',
    description: 'Open-source model with balanced capabilities',
    capabilities: ['open-source', 'balanced', 'general'],
    icon: <SmartToy />,
    color: '#ff3860',
    contextWindow: 80000,
    isActive: true
  }
};

// Specialized Roles Configuration
const SPECIALIZED_ROLES = {
  RED_TEAM: {
    id: 'RED_TEAM',
    name: 'Red Team',
    description: 'Critiques and finds flaws in proposals',
    icon: <Psychology />,
    color: '#ff3860'
  },
  DEVILS_ADVOCATE: {
    id: 'DEVILS_ADVOCATE',
    name: 'Devil\'s Advocate',
    description: 'Challenges assumptions and consensus thinking',
    icon: <Science />,
    color: '#8000ff'
  },
  VERIFICATION_AGENT: {
    id: 'VERIFICATION_AGENT',
    name: 'Verification Agent',
    description: 'Fact-checks all outputs against reliable sources',
    icon: <ModelTraining />,
    color: '#00f2ff'
  }
};

// Main Component
const CognitiveOrchestrationEngine = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [inputText, setInputText] = useState('');
  const [activeModels, setActiveModels] = useState(['GPT4', 'CLAUDE3', 'GEMINI']);
  const [specializedRoles, setSpecializedRoles] = useState(['VERIFICATION_AGENT']);
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState(null);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [capabilitiesDialogOpen, setCapabilitiesDialogOpen] = useState(false);
  const [selectedCapability, setSelectedCapability] = useState('synthesized_consensus');

  // Sample processing simulation
  const simulateProcessing = () => {
    setIsProcessing(true);
    setProcessingProgress(0);
    
    const interval = setInterval(() => {
      setProcessingProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsProcessing(false);
          
          // Set sample results after processing
          setResults({
            synthesizedOutput: `Based on analysis from ${activeModels.length} AI models, here's the comprehensive strategy for launching a sustainable energy startup in the EU:

1. **Market Entry Strategy**: Focus initially on Germany and Spain due to favorable regulatory environments and subsidy programs.

2. **Regulatory Compliance**: The revised Renewable Energy Directive (RED III) requires specific certifications for grid-connected systems. Plan for a 3-4 month certification process.

3. **Funding Opportunities**: Apply for the EU Innovation Fund which has €40 billion available for clean tech projects through 2030.

4. **Technology Focus**: Solar-integrated solutions with battery storage show the highest ROI in Southern European markets.

5. **Partnership Strategy**: Collaborate with local installers to navigate country-specific regulations and leverage established distribution networks.

All models agree on the market potential, with Claude 3 providing particularly detailed regulatory analysis and GPT-4 offering innovative business model suggestions.`,
            modelContributions: {
              GPT4: 35,
              CLAUDE3: 30,
              GEMINI: 25,
              DEEPSEEK: 10
            },
            confidence: 92,
            heatmap: [
              { text: 'Market potential in Southern Europe', agreement: 'high' },
              { text: 'Initial focus on Germany vs Spain', agreement: 'medium' },
              { text: 'Regulatory timeline estimates', agreement: 'low' }
            ],
            processingTime: 8.4,
            tokensProcessed: 24582,
            costEstimate: 0.47
          });
          return 100;
        }
        return prev + (100 / 30); // Complete in 30 steps
      });
    }, 100);
  };

  // Handle model drag and drop
  const moveModel = (fromIndex, toIndex) => {
    const updatedModels = [...activeModels];
    const [movedItem] = updatedModels.splice(fromIndex, 1);
    updatedModels.splice(toIndex, 0, movedItem);
    setActiveModels(updatedModels);
  };

  // Add model to active council
  const addModel = (modelId) => {
    if (!activeModels.includes(modelId)) {
      setActiveModels([...activeModels, modelId]);
    }
  };

  // Remove model from active council
  const removeModel = (modelId) => {
    setActiveModels(activeModels.filter(id => id !== modelId));
  };

  // Toggle specialized role
  const toggleRole = (roleId) => {
    if (specializedRoles.includes(roleId)) {
      setSpecializedRoles(specializedRoles.filter(id => id !== roleId));
    } else {
      setSpecializedRoles([...specializedRoles, roleId]);
    }
  };

  // Prepare data for contribution chart
  const contributionData = {
    labels: ['Accuracy', 'Creativity', 'Depth', 'Clarity', 'Relevance', 'Uniqueness'],
    datasets: [
      {
        label: 'GPT-4',
        data: [95, 92, 88, 90, 94, 85],
        borderColor: '#00f2ff',
        backgroundColor: 'rgba(0, 242, 255, 0.2)',
        pointBackgroundColor: '#00f2ff'
      },
      {
        label: 'Claude 3',
        data: [98, 85, 97, 95, 96, 80],
        borderColor: '#ff00d6',
        backgroundColor: 'rgba(255, 0, 214, 0.2)',
        pointBackgroundColor: '#ff00d6'
      },
      {
        label: 'Gemini',
        data: [90, 88, 92, 87, 98, 92],
        borderColor: '#ffcc00',
        backgroundColor: 'rgba(255, 204, 0, 0.2)',
        pointBackgroundColor: '#ffcc00'
      }
    ]
  };

  const chartOptions = {
    scales: {
      r: {
        angleLines: {
          color: 'rgba(255, 255, 255, 0.1)'
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        },
        pointLabels: {
          color: '#fff'
        },
        ticks: {
          backdropColor: 'transparent',
          color: 'rgba(255, 255, 255, 0.7)'
        }
      }
    },
    plugins: {
      legend: {
        labels: {
          color: '#fff'
        }
      }
    }
  };

  // Draggable Model Component
  const DraggableModel = ({ modelId, index, moveModel }) => {
    const ref = useRef(null);
    
    const [{ isDragging }, drag] = useDrag({
      type: 'model',
      item: { index },
      collect: (monitor) => ({
        isDragging: monitor.isDragging()
      })
    });
    
    const [, drop] = useDrop({
      accept: 'model',
      hover: (item) => {
        if (item.index !== index) {
          moveModel(item.index, index);
          item.index = index;
        }
      }
    });
    
    drag(drop(ref));
    
    const model = AI_MODELS[modelId];
    
    return (
      <Card 
        ref={ref}
        sx={{ 
          mb: 1, 
          opacity: isDragging ? 0.5 : 1,
          cursor: 'move',
          background: 'rgba(30, 30, 60, 0.5)',
          border: `1px solid ${model.color}40`,
          '&:hover': {
            border: `1px solid ${model.color}`,
            transform: 'translateY(-2px)',
            boxShadow: `0 5px 15px ${model.color}30`
          }
        }}
      >
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box display="flex" alignItems="center">
            <DragIndicator sx={{ mr: 1, color: 'text.secondary' }} />
            <Box sx={{ color: model.color, mr: 1 }}>{model.icon}</Box>
            <Typography variant="body2" sx={{ fontWeight: 'medium', flexGrow: 1 }}>
              {model.name}
            </Typography>
            <IconButton size="small" onClick={() => removeModel(modelId)}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>✕</Typography>
            </IconButton>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <Box sx={{ 
        flexGrow: 1, 
        background: 'linear-gradient(135deg, #0a0e17 0%, #131a2a 100%)',
        color: 'white',
        minHeight: '100vh',
        p: isMobile ? 1 : 2
      }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 2,
          p: 2,
          background: 'rgba(13, 18, 31, 0.95)',
          borderRadius: 2,
          borderBottom: '1px solid rgba(0, 242, 255, 0.2)'
        }}>
          <Box display="flex" alignItems="center">
            <AutoAwesome sx={{ mr: 1, color: '#00f2ff' }} />
            <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold' }}>
              Cognitive Orchestration Engine
            </Typography>
          </Box>
          <Box>
            <IconButton sx={{ color: 'white' }} onClick={() => setSettingsOpen(true)}>
              <Settings />
            </IconButton>
            <Tooltip title="Help and documentation">
              <IconButton sx={{ color: 'white' }}>
                <Help />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Grid container spacing={2}>
          {/* Left Panel - Model Selection */}
          <Grid item xs={12} md={3}>
            <Card sx={{ 
              background: 'rgba(19, 26, 42, 0.9)', 
              height: '100%',
              borderRadius: 2,
              border: '1px solid rgba(0, 242, 255, 0.2)'
            }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: '#00f2ff' }}>
                  Available Models
                </Typography>
                
                {Object.values(AI_MODELS).map(model => (
                  <Card 
                    key={model.id}
                    sx={{ 
                      mb: 1, 
                      cursor: 'pointer',
                      background: 'rgba(28, 36, 56, 0.7)',
                      border: activeModels.includes(model.id) ? `1px solid ${model.color}` : '1px solid transparent',
                      '&:hover': {
                        border: `1px solid ${model.color}`,
                        transform: 'translateY(-2px)',
                        boxShadow: `0 5px 15px ${model.color}30`
                      }
                    }}
                    onClick={() => addModel(model.id)}
                  >
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box display="flex" alignItems="center">
                        <Box sx={{ color: model.color, mr: 1 }}>{model.icon}</Box>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {model.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {model.description}
                          </Typography>
                        </Box>
                        {activeModels.includes(model.id) && (
                          <Chip label="Active" size="small" sx={{ background: model.color, color: 'black', fontSize: '0.6rem' }} />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                ))}
                
                <Typography variant="h6" gutterBottom sx={{ mt: 3, color: '#ffcc00' }}>
                  Specialized Roles
                </Typography>
                
                {Object.values(SPECIALIZED_ROLES).map(role => (
                  <Card 
                    key={role.id}
                    sx={{ 
                      mb: 1, 
                      cursor: 'pointer',
                      background: 'rgba(28, 36, 56, 0.7)',
                      border: specializedRoles.includes(role.id) ? `1px solid ${role.color}` : '1px solid transparent',
                      '&:hover': {
                        border: `1px solid ${role.color}`,
                        transform: 'translateY(-2px)'
                      }
                    }}
                    onClick={() => toggleRole(role.id)}
                  >
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box display="flex" alignItems="center">
                        <Box sx={{ color: role.color, mr: 1 }}>{role.icon}</Box>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {role.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {role.description}
                          </Typography>
                        </Box>
                        {specializedRoles.includes(role.id) && (
                          <Chip label="Active" size="small" sx={{ background: role.color, color: 'black', fontSize: '0.6rem' }} />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </CardContent>
            </Card>
          </Grid>

          {/* Center Panel - Input and Output */}
          <Grid item xs={12} md={6}>
            <Card sx={{ 
              background: 'rgba(13, 18, 31, 0.9)', 
              height: '100%',
              borderRadius: 2,
              border: '1px solid rgba(255, 255, 255, 0.05)'
            }}>
              <CardContent>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Enter your query or task for the AI council..."
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  sx={{ 
                    mb: 2,
                    background: 'rgba(28, 36, 56, 0.7)',
                    borderRadius: 1,
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.1)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 242, 255, 0.3)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#00f2ff',
                      },
                    },
                  }}
                />
                
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Box>
                    <Button 
                      startIcon={<Upload />} 
                      sx={{ mr: 1, color: 'white', borderColor: 'rgba(255, 255, 255, 0.2)' }} 
                      variant="outlined"
                    >
                      Attach Files
                    </Button>
                    <Button 
                      startIcon={<Settings />} 
                      sx={{ color: 'white', borderColor: 'rgba(255, 255, 255, 0.2)' }} 
                      variant="outlined"
                      onClick={() => setCapabilitiesDialogOpen(true)}
                    >
                      Capabilities
                    </Button>
                  </Box>
                  <Button 
                    startIcon={<PlayArrow />} 
                    variant="contained"
                    onClick={simulateProcessing}
                    disabled={isProcessing || inputText.trim() === ''}
                    sx={{
                      background: 'linear-gradient(90deg, #00f2ff, #ff00d6)',
                      '&:hover': {
                        background: 'linear-gradient(90deg, #00c2ff, #d600ff)',
                      }
                    }}
                  >
                    Initiate Cognitive Orchestration
                  </Button>
                </Box>
                
                {isProcessing && (
                  <Box sx={{ mb: 2 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={processingProgress} 
                      sx={{ 
                        height: 10, 
                        borderRadius: 5,
                        background: 'rgba(255, 255, 255, 0.1)',
                        '& .MuiLinearProgress-bar': {
                          background: 'linear-gradient(90deg, #00f2ff, #ff00d6)',
                        }
                      }} 
                    />
                    <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                      Processing... {Math.round(processingProgress)}%
                    </Typography>
                  </Box>
                )}
                
                <Box sx={{ 
                  minHeight: 300, 
                  background: 'rgba(19, 26, 42, 0.7)', 
                  borderRadius: 2, 
                  p: 2,
                  border: '1px solid rgba(255, 255, 255, 0.05)'
                }}>
                  {results ? (
                    <>
                      <Typography variant="h6" gutterBottom sx={{ color: '#00f2ff' }}>
                        Synthesized Consensus
                      </Typography>
                      
                      {/* Consensus Heatmap */}
                      <Box sx={{ mb: 2 }}>
                        {results.heatmap.map((item, index) => (
                          <Chip
                            key={index}
                            label={item.text}
                            sx={{ 
                              mr: 1, 
                              mb: 1,
                              background: 
                                item.agreement === 'high' ? 'rgba(0, 255, 157, 0.2)' :
                                item.agreement === 'medium' ? 'rgba(255, 204, 0, 0.2)' :
                                'rgba(255, 56, 96, 0.2)',
                              color: 'white',
                              border: 
                                item.agreement === 'high' ? '1px solid #00ff9d' :
                                item.agreement === 'medium' ? '1px solid #ffcc00' :
                                '1px solid #ff3860'
                            }}
                          />
                        ))}
                      </Box>
                      
                      <Typography variant="body2" paragraph sx={{ whiteSpace: 'pre-wrap' }}>
                        {results.synthesizedOutput}
                      </Typography>
                      
                      <Box display="flex" mt={2}>
                        <Button startIcon={<Download />} variant="outlined" size="small" sx={{ mr: 1, color: 'white' }}>
                          Export
                        </Button>
                        <Button startIcon={<ContentCopy />} variant="outlined" size="small" sx={{ mr: 1, color: 'white' }}>
                          Copy
                        </Button>
                        <Button startIcon={<Share />} variant="outlined" size="small" sx={{ color: 'white' }}>
                          Share
                        </Button>
                      </Box>
                    </>
                  ) : (
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height={300}>
                      <AutoAwesome sx={{ fontSize: 48, color: 'rgba(255, 255, 255, 0.2)', mb: 2 }} />
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', textAlign: 'center' }}>
                        {inputText ? 'Ready to process your query with the AI council' : 'Enter a query to begin cognitive orchestration'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Panel - Active Council and Analysis */}
          <Grid item xs={12} md={3}>
            <Card sx={{ 
              background: 'rgba(19, 26, 42, 0.9)', 
              height: '100%',
              borderRadius: 2,
              border: '1px solid rgba(0, 242, 255, 0.2)'
            }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: '#ffcc00' }}>
                  Active Council
                </Typography>
                
                <Box sx={{ 
                  minHeight: 150, 
                  background: 'rgba(28, 36, 56, 0.3)', 
                  borderRadius: 1, 
                  p: 1,
                  border: '2px dashed rgba(0, 242, 255, 0.3)',
                  mb: 2
                }}>
                  {activeModels.length > 0 ? (
                    activeModels.map((modelId, index) => (
                      <DraggableModel
                        key={modelId}
                        modelId={modelId}
                        index={index}
                        moveModel={moveModel}
                      />
                    ))
                  ) : (
                    <Box display="flex" alignItems="center" justifyContent="center" height={150}>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                        Drag models here to form your council
                      </Typography>
                    </Box>
                  )}
                </Box>
                
                {results && (
                  <>
                    <Typography variant="h6" gutterBottom sx={{ color: '#00f2ff' }}>
                      Analysis
                    </Typography>
                    
                    <Box sx={{ background: 'rgba(28, 36, 56, 0.5)', borderRadius: 2, p: 2, mb: 2 }}>
                      <Typography variant="body2" gutterBottom sx={{ fontWeight: 'medium' }}>
                        Confidence Score
                      </Typography>
                      <Box display="flex" alignItems="center">
                        <Box sx={{ flexGrow: 1, mr: 1 }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={results.confidence} 
                            sx={{ 
                              height: 10, 
                              borderRadius: 5,
                              background: 'rgba(255, 255, 255, 0.1)',
                              '& .MuiLinearProgress-bar': {
                                background: 'linear-gradient(90deg, #00f2ff, #00ff9d)',
                              }
                            }} 
                          />
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {results.confidence}%
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box sx={{ background: 'rgba(28, 36, 56, 0.5)', borderRadius: 2, p: 2, mb: 2 }}>
                      <Typography variant="body2" gutterBottom sx={{ fontWeight: 'medium' }}>
                        Model Contributions
                      </Typography>
                      <Box sx={{ height: 150 }}>
                        <Radar data={contributionData} options={chartOptions} />
                      </Box>
                    </Box>
                    
                    <Box sx={{ background: 'rgba(28, 36, 56, 0.5)', borderRadius: 2, p: 2 }}>
                      <Typography variant="body2" gutterBottom sx={{ fontWeight: 'medium' }}>
                        Performance Metrics
                      </Typography>
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption">Processing Time:</Typography>
                        <Typography variant="caption">{results.processingTime}s</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption">Tokens Processed:</Typography>
                        <Typography variant="caption">{results.tokensProcessed.toLocaleString()}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="caption">Estimated Cost:</Typography>
                        <Typography variant="caption">${results.costEstimate}</Typography>
                      </Box>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Settings Drawer */}
        <Drawer
          anchor="right"
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          PaperProps={{
            sx: {
              background: 'rgba(13, 18, 31, 0.98)',
              color: 'white',
              width: 350
            }
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Engine Settings
            </Typography>
            
            <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
              Output Preferences
            </Typography>
            
            <Typography variant="body2" sx={{ mt: 3, mb: 1 }}>
              Model Parameters
            </Typography>
            
            <Typography variant="body2" sx={{ mt: 3, mb: 1 }}>
              Advanced Settings
            </Typography>
            
            <Button variant="outlined" fullWidth sx={{ mt: 3, color: 'white' }}>
              Save Settings
            </Button>
          </Box>
        </Drawer>

        {/* Capabilities Dialog */}
        <Dialog 
          open={capabilitiesDialogOpen} 
          onClose={() => setCapabilitiesDialogOpen(false)}
          PaperProps={{
            sx: {
              background: 'rgba(13, 18, 31, 0.95)',
              color: 'white',
              borderRadius: 2
            }
          }}
        >
          <DialogTitle>Select Capability Mode</DialogTitle>
          <DialogContent>
            <List>
              <ListItem 
                button 
                selected={selectedCapability === 'synthesized_consensus'}
                onClick={() => setSelectedCapability('synthesized_consensus')}
              >
                <ListItemIcon>
                  <AutoAwesome sx={{ color: '#00f2ff' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Synthesized Consensus" 
                  secondary="Combine insights from all models into a unified response" 
                />
              </ListItem>
              
              <ListItem 
                button 
                selected={selectedCapability === 'scenario_planning'}
                onClick={() => setSelectedCapability('scenario_planning')}
              >
                <ListItemIcon>
                  <Analytics sx={{ color: '#ffcc00' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Multi-Perspective Scenario Planning" 
                  secondary="Generate optimistic, pessimistic, and most likely scenarios" 
                />
              </ListItem>
              
              <ListItem 
                button 
                selected={selectedCapability === 'debugging'}
                onClick={() => setSelectedCapability('debugging')}
              >
                <ListItemIcon>
                  <IntegrationInstructions sx={{ color: '#00ff9d' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Multi-Layer Debugging" 
                  secondary="Analyze problems from code, logs, and architecture perspectives" 
                />
              </ListItem>
              
              <ListItem 
                button 
                selected={selectedCapability === 'learning'}
                onClick={() => setSelectedCapability('learning')}
              >
                <ListItemIcon>
                  <Psychology sx={{ color: '#ff00d6' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Socratic Learning Dialogue" 
                  secondary="Interactive learning through questions and explanations" 
                />
              </ListItem>
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCapabilitiesDialogOpen(false)} sx={{ color: 'white' }}>
              Cancel
            </Button>
            <Button 
              onClick={() => setCapabilitiesDialogOpen(false)}
              sx={{ background: '#00f2ff', color: 'black', '&:hover': { background: '#00c2ff' } }}
            >
              Apply
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DndProvider>
  );
};

export default CognitiveOrchestrationEngine;